/* eslint-disable */

/**
 * Resultado da validação de peso
 */
export interface WeightValidationResult {
  isValid: boolean
  status: 'valid' | 'warning' | 'blocked'
  message?: string
  details?: {
    weight: number
    ageInMonths: number
    sex: string
    minLimit: number
    maxLimit: number
    warningMinLimit: number
    warningMaxLimit: number
  }
}

/**
 * Range de peso para uma faixa de idade
 */
interface WeightRange {
  minAge: number
  maxAge: number | null // null = sem limite superior
  minWeight: number
  maxWeight: number
}

/**
 * Ranges de peso por idade e sexo (em meses)
 */
const WEIGHT_RANGES: Record<'default' | 'female' | 'male', WeightRange[]> = {
  default: [
    { minAge: 0, maxAge: 4, minWeight: 15, maxWeight: 95 },
    { minAge: 4, maxAge: 7, minWeight: 50, maxWeight: 200 },
    { minAge: 7, maxAge: 12, minWeight: 90, maxWeight: 300 },
    { minAge: 12, maxAge: 15, minWeight: 130, maxWeight: 400 },
    { minAge: 15, maxAge: 30, minWeight: 150, maxWeight: 500 }
  ],
  female: [{ minAge: 30, maxAge: null, minWeight: 280, maxWeight: 1500 }],
  male: [{ minAge: 30, maxAge: null, minWeight: 380, maxWeight: 2000 }]
}

/**
 * Margem de tolerância para avisos (5% dos limites)
 */
const WARNING_THRESHOLD_PERCENTAGE = 0.05

/**
 * Calcula a idade em meses a partir da data de nascimento
 */
export const calculateAgeInMonths = (birthDate: string | Date): number => {
  const birth = typeof birthDate === 'string' ? new Date(birthDate) : birthDate
  const today = new Date()

  const yearsDiff = today.getFullYear() - birth.getFullYear()
  const monthsDiff = today.getMonth() - birth.getMonth()

  return yearsDiff * 12 + monthsDiff
}

/**
 * Obtém o range de peso apropriado para a idade e sexo
 */
const getWeightRange = (ageInMonths: number, sex: string): WeightRange | null => {
  // Normaliza o sexo
  const normalizedSex = sex.toLowerCase()
  const isFemale = normalizedSex === 'femea' || normalizedSex === 'fêmea' || normalizedSex === 'f'
  const isMale = normalizedSex === 'macho' || normalizedSex === 'm'

  // Para animais acima de 30 meses, usa ranges específicos por sexo
  if (ageInMonths >= 30) {
    if (isFemale) {
      return WEIGHT_RANGES.female[0]
    }
    if (isMale) {
      return WEIGHT_RANGES.male[0]
    }
  }

  // Para animais abaixo de 30 meses, usa ranges padrão
  const range = WEIGHT_RANGES.default.find(
    (r) => ageInMonths >= r.minAge && (r.maxAge === null || ageInMonths < r.maxAge)
  )

  return range || null
}

/**
 * Valida o peso do animal com base na idade e sexo
 *
 * @param weight - Peso do animal em kg
 * @param birthDate - Data de nascimento do animal
 * @param sex - Sexo do animal ('Macho', 'Fêmea', 'M', 'F')
 * @returns Resultado da validação
 */
export const validateAnimalWeight = (weight: number, birthDate: string | Date, sex: string): WeightValidationResult => {
  // Calcula a idade em meses
  const ageInMonths = calculateAgeInMonths(birthDate)

  // Obtém o range apropriado
  const range = getWeightRange(ageInMonths, sex)

  if (!range) {
    return {
      isValid: false,
      status: 'blocked',
      message: 'Não foi possível determinar o range de peso para esta idade e sexo.'
    }
  }

  const { minWeight, maxWeight } = range

  // Calcula limites com margem de aviso (±5%)
  const warningMinLimit = minWeight * (1 - WARNING_THRESHOLD_PERCENTAGE)
  const warningMaxLimit = maxWeight * (1 + WARNING_THRESHOLD_PERCENTAGE)

  const details = {
    weight,
    ageInMonths,
    sex,
    minLimit: minWeight,
    maxLimit: maxWeight,
    warningMinLimit,
    warningMaxLimit
  }

  // Peso biologicamente absurdo (muito fora dos limites)
  if (weight < warningMinLimit || weight > warningMaxLimit) {
    return {
      isValid: false,
      status: 'blocked',
      message: `Peso ${weight}kg está fora dos limites biologicamente aceitáveis para um animal de ${ageInMonths} meses (${sex}). Range esperado: ${minWeight}-${maxWeight}kg.`,
      details
    }
  }

  // Peso próximo das bordas (dentro da margem de aviso)
  if ((weight >= warningMinLimit && weight < minWeight) || (weight > maxWeight && weight <= warningMaxLimit)) {
    return {
      isValid: true,
      status: 'warning',
      message: `Peso ${weight}kg está próximo dos limites para um animal de ${ageInMonths} meses (${sex}). Range esperado: ${minWeight}-${maxWeight}kg. Deseja continuar?`,
      details
    }
  }

  // Peso dentro do range normal
  return {
    isValid: true,
    status: 'valid',
    details
  }
}

/**
 * Formata a mensagem de erro/aviso de peso
 */
export const formatWeightValidationMessage = (result: WeightValidationResult): string => {
  if (!result.details) {
    return result.message || 'Erro ao validar peso.'
  }

  const { weight, ageInMonths, sex, minLimit, maxLimit } = result.details

  if (result.status === 'blocked') {
    return `O peso informado (${weight}kg) está biologicamente inconsistente para um animal de ${ageInMonths} meses (${sex}). O peso esperado deve estar entre ${minLimit}kg e ${maxLimit}kg.`
  }

  if (result.status === 'warning') {
    return `O peso informado (${weight}kg) está próximo dos limites para um animal de ${ageInMonths} meses (${sex}). Range esperado: ${minLimit}-${maxLimit}kg. Tem certeza que deseja continuar?`
  }

  return 'Peso válido.'
}
